import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import api, { mediaUrl, extractErr } from "@/lib/api";
import { useCustomerAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronRight, Download, LogOut, Package, UserRound, Wallet } from "lucide-react";

export default function StorefrontAccount({ config }) {
  const { t } = useTranslation();
  const { slug, base } = useStoreBase();
  const { user, loading, logout } = useCustomerAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [payout, setPayout] = useState({ method: "upi", upi: "", account_name: "", account_number: "", ifsc: "" });
  const [wdAmount, setWdAmount] = useState("");
  const referOn = Boolean(config.customer_refer_active);

  useEffect(() => {
    if (!user) return;
    api.get(`/store/${slug}/my-orders`).then(({ data }) => setOrders(data || [])).catch(() => {});
    api.get(`/store/${slug}/my-downloads`).then(({ data }) => setDownloads(data.items || [])).catch(() => {});
    if (referOn) {
      api.get(`/store/${slug}/wallet`).then(({ data }) => {
        setWallet(data);
        if (data.payout) setPayout({ method: "upi", upi: "", account_name: "", account_number: "", ifsc: "", ...data.payout });
      }).catch(() => {});
    }
  }, [slug, user, referOn]);

  const setTab = (t) => {
    const next = new URLSearchParams(params);
    if (t === "overview") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: true });
  };

  const fileHref = (url) => {
    if (!url) return null;
    return mediaUrl(url);
  };

  const tabs = useMemo(() => ([
    { id: "overview", label: t("store.overview"), icon: UserRound },
    { id: "orders", label: t("store.orders"), icon: Package },
    { id: "downloads", label: t("store.downloads"), icon: Download },
    ...(referOn ? [{ id: "wallet", label: "Wallet", icon: Wallet }] : []),
  ]), [t, referOn]);

  const signOut = async () => {
    await logout();
    toast.success(t("common.signedOut"));
  };
  const initial = (user?.name || user?.email || "?").trim().slice(0, 1).toUpperCase();

  if (loading) return <div className="p-12 text-center text-stone-500">{t("common.loading")}</div>;
  if (!user) return <Navigate to={`${base}/account/login`} replace />;

  return (
    <div className="bg-stone-50 min-h-[70vh]" data-testid="store-account-page">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="rounded-3xl p-6 sm:p-8 text-white shadow-lg flex flex-col sm:flex-row sm:items-center gap-5" style={{ background: "linear-gradient(135deg, var(--store-primary, #15803D), #1c1917)" }}>
        <div className="w-16 h-16 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center text-2xl font-extrabold shrink-0" data-testid="account-avatar">{initial}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-semibold">{t("store.myAccount")}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold truncate">{user.name || t("store.myAccount")}</h1>
          <p className="text-sm text-white/80 mt-1 truncate">{t("store.signedInAs", { who: user.email || user.name })}</p>
        </div>
        <button type="button" onClick={signOut} data-testid="account-logout" className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-white text-stone-900 text-sm font-bold shrink-0">
          <LogOut className="w-4 h-4" /> {t("store.signOut")}
        </button>
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            data-testid={`account-tab-${item.id}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border ${tab === item.id ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200 hover:text-stone-900"}`}
          >
            <item.icon className="w-4 h-4" /> {item.label}
            {item.id === "downloads" && downloads.length > 0 && (
              <span className={`text-[10px] font-mono-nums rounded-full px-1.5 ${tab === item.id ? "bg-white/20" : "bg-stone-100"}`}>{downloads.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Card className="p-5 border-stone-200/70 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 inline-flex items-center justify-center"><Package className="w-5 h-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500">Orders</div>
                <div className="font-display text-2xl font-bold leading-none mt-1">{orders.length}</div>
              </div>
            </div>
            <button type="button" className="text-sm font-semibold text-stone-800 mt-4 inline-flex items-center gap-1" onClick={() => setTab("orders")}>View orders <ChevronRight className="w-4 h-4" /></button>
          </Card>
          <Card className="p-5 border-stone-200/70 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-2xl bg-violet-50 text-violet-700 inline-flex items-center justify-center"><Download className="w-5 h-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500">Digital downloads</div>
                <div className="font-display text-2xl font-bold leading-none mt-1">{downloads.length}</div>
              </div>
            </div>
            <button type="button" className="text-sm font-semibold text-stone-800 mt-4 inline-flex items-center gap-1" onClick={() => setTab("downloads")}>Open files <ChevronRight className="w-4 h-4" /></button>
          </Card>
          {referOn && wallet ? (
            <Card className="p-5 border-stone-200/70 shadow-sm sm:col-span-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-stone-500">Wallet balance</div>
                  <div className="font-display text-2xl font-bold mt-1">₹{Number(wallet.balance || 0).toLocaleString("en-IN")}</div>
                </div>
                <button type="button" className="text-sm font-semibold inline-flex items-center gap-1" onClick={() => setTab("wallet")}>Open wallet <ChevronRight className="w-4 h-4" /></button>
              </div>
            </Card>
          ) : null}
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 space-y-3">
          {orders.length === 0 ? (
            <Card className="p-8 text-center text-stone-500 text-sm border-stone-200/70">No orders yet.</Card>
          ) : orders.map((o) => (
            <Link to={`${base}/order/${o.id}`} key={o.id}>
              <Card className="p-4 border-stone-200/70 hover:shadow-md hover:border-stone-300 flex items-center gap-4 transition-shadow" data-testid={`my-order-${o.order_number}`}>
                <span className="w-11 h-11 rounded-2xl bg-stone-100 text-stone-700 inline-flex items-center justify-center shrink-0"><Package className="w-5 h-5" /></span>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-nums font-semibold text-stone-900">{o.order_number}</div>
                  <div className="text-xs text-stone-500 truncate">{o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : ""} · {o.items?.length || 0} items</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-nums font-semibold">₹{(o.total || 0).toLocaleString("en-IN")}</div>
                  <Badge className="bg-stone-100 text-stone-700 border-0 capitalize mt-1">{o.status}</Badge>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400 hidden sm:block" />
              </Card>
            </Link>
          ))}
        </div>
      )}

      {tab === "downloads" && (
        <div className="mt-6 space-y-3">
          {downloads.length === 0 ? (
            <Card className="p-8 text-center text-stone-500 text-sm border-stone-200/70">
              No digital files yet. When you buy a digital product, it will appear here.
            </Card>
          ) : downloads.map((d, i) => {
            const href = fileHref(d.file_url) || d.link;
            return (
              <Card key={`${d.order_id}-${d.product_id}-${i}`} className="p-4 border-stone-200/70 flex items-center gap-4" data-testid={`download-${d.product_id}`}>
                <div className="w-12 h-12 rounded-lg bg-stone-100 overflow-hidden shrink-0">
                  {d.image ? <img src={mediaUrl(d.image)} alt="" className="w-full h-full object-cover" /> : <Download className="w-5 h-5 m-3.5 text-stone-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-stone-900 truncate">{d.name}</div>
                  <div className="text-xs text-stone-500">Order {d.order_number}</div>
                </div>
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="rounded-full btn-store-primary" data-testid={`download-open-${d.product_id}`}>
                      <Download className="w-4 h-4 mr-1" /> {d.file_url ? "Download" : "Open link"}
                    </Button>
                  </a>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      {tab === "wallet" && wallet && (
        <div className="mt-6 space-y-4" data-testid="account-wallet">
          <Card className="p-5 border-stone-200/70">
            <div className="text-xs uppercase tracking-wider text-stone-500">Balance</div>
            <div className="font-display text-3xl font-bold mt-1">₹{Number(wallet.balance || 0).toLocaleString("en-IN")}</div>
            <div className="mt-4">
              <div className="text-xs text-stone-500">Your referral link (direct only)</div>
              <div className="font-mono-nums text-sm mt-1 break-all" data-testid="account-referral-link">{`${window.location.origin}${base}/account/signup?ref=${wallet.referral_code}`}</div>
            </div>
          </Card>
          {wallet.can_withdraw && (
            <Card className="p-5 space-y-3 border-stone-200/70">
              <div className="font-semibold">Payout details</div>
              <select className="w-full h-10 rounded-md border px-3 text-sm" value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })}>
                <option value="upi">UPI</option>
                <option value="bank">Bank account</option>
              </select>
              {payout.method === "upi" ? (
                <div><Label>UPI ID</Label><Input className="mt-1" value={payout.upi} onChange={(e) => setPayout({ ...payout, upi: e.target.value })} data-testid="wallet-upi" /></div>
              ) : (
                <>
                  <div><Label>Account name</Label><Input className="mt-1" value={payout.account_name} onChange={(e) => setPayout({ ...payout, account_name: e.target.value })} /></div>
                  <div><Label>Account number</Label><Input className="mt-1" value={payout.account_number} onChange={(e) => setPayout({ ...payout, account_number: e.target.value })} /></div>
                  <div><Label>IFSC</Label><Input className="mt-1" value={payout.ifsc} onChange={(e) => setPayout({ ...payout, ifsc: e.target.value })} /></div>
                </>
              )}
              <Button type="button" variant="outline" onClick={async () => {
                try { await api.put(`/store/${slug}/wallet/payout`, payout); toast.success("Saved"); }
                catch (e) { toast.error(extractErr(e)); }
              }}>Save payout details</Button>
              <div className="pt-2 border-t">
                <Label>Withdraw amount</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="number" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} data-testid="wallet-withdraw-amount" />
                  <Button type="button" data-testid="wallet-withdraw" onClick={async () => {
                    try {
                      await api.post(`/store/${slug}/wallet/withdraw`, { amount: Number(wdAmount) });
                      toast.success("Withdrawal requested");
                      const { data } = await api.get(`/store/${slug}/wallet`);
                      setWallet(data);
                    } catch (e) { toast.error(extractErr(e)); }
                  }}>Request</Button>
                </div>
                <p className="text-xs text-stone-500 mt-1">Minimum ₹{wallet.settings?.min_withdraw}</p>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
    </div>
  );
}
